/**
 * Settings that follow the account (Phase 8): key and button bindings, the gamepad, the audio
 * levels and the accessibility choices. Graphics settings stay per device. The client keeps
 * these inside its settings blob; the API stores the same shape in `user_settings`, so the
 * schema is shared and validated on both sides.
 */
import { z } from 'zod';
export const ACTIONS = [
    'moveForward',
    'moveBack',
    'strafeLeft',
    'strafeRight',
    'boost',
    'jump',
    'primary',
    'secondary',
    'ability',
    'ultimate',
    'vent',
    'melee',
    'swapShoulder',
    'scoreboard',
    'menu',
    'stats',
];
export const ACTION_LABELS = {
    moveForward: 'Move forward',
    moveBack: 'Move back',
    strafeLeft: 'Strafe left',
    strafeRight: 'Strafe right',
    boost: 'Boost',
    jump: 'Jump / jets',
    primary: 'Fire primary',
    secondary: 'Fire secondary',
    ability: 'Ability',
    ultimate: 'Ultimate',
    vent: 'Vent heat',
    melee: 'Melee',
    swapShoulder: 'Swap shoulder',
    scoreboard: 'Scoreboard (hold)',
    menu: 'Menu',
    stats: 'Frame-time overlay',
};
/** A `KeyboardEvent.code` or a mouse button (`Mouse0` left, `Mouse1` middle, `Mouse2` right). */
export const BindingSchema = z
    .string()
    .regex(/^(Mouse[0-4]|(?!Mouse)[A-Z][A-Za-z0-9]{1,24})$/, 'a key code such as KeyW, or MouseN');
/** Up to two bindings per action (a key and an alternate). */
export const BindingsSchema = z
    .object(Object.fromEntries(ACTIONS.map((a) => [a, z.array(BindingSchema).min(1).max(2)])))
    .strict();
export const DEFAULT_BINDINGS = {
    moveForward: ['KeyW', 'ArrowUp'],
    moveBack: ['KeyS', 'ArrowDown'],
    strafeLeft: ['KeyA', 'ArrowLeft'],
    strafeRight: ['KeyD', 'ArrowRight'],
    boost: ['ShiftLeft', 'ShiftRight'],
    jump: ['Space'],
    primary: ['Mouse0'],
    secondary: ['Mouse2'],
    ability: ['KeyQ'],
    ultimate: ['KeyE'],
    vent: ['KeyR'],
    melee: ['KeyF'],
    swapShoulder: ['KeyC'],
    scoreboard: ['Tab'],
    menu: ['Escape'],
    stats: ['F3'],
};
export const GamepadSettingsSchema = z
    .object({
    enabled: z.boolean(),
    /** Right-stick aim rate multiplier. */
    sensitivity: z.number().min(0.2).max(3),
    deadZone: z.number().min(0).max(0.5),
})
    .strict();
export const DEFAULT_GAMEPAD = { enabled: true, sensitivity: 1, deadZone: 0.15 };
/** Bus levels 0..1; the engine squares them so the middle of a slider is half as loud. */
export const AudioLevelsSchema = z
    .object({
    master: z.number().min(0).max(1),
    sfx: z.number().min(0).max(1),
    music: z.number().min(0).max(1),
    ui: z.number().min(0).max(1),
})
    .strict();
export const DEFAULT_AUDIO = { master: 0.8, sfx: 1, music: 0.6, ui: 0.8 };
export const PALETTES = ['default', 'deuteranopia', 'protanopia', 'tritanopia'];
/** A colour palette for the HUD's sides and bars, captions for sound cues, a shake scale. */
export const AccessibilitySchema = z
    .object({
    palette: z.enum(PALETTES),
    captions: z.boolean(),
    screenShake: z.number().min(0).max(1),
})
    .strict();
export const DEFAULT_ACCESSIBILITY = {
    palette: 'default',
    captions: false,
    screenShake: 1,
};
export const UserSettingsSchema = z
    .object({
    bindings: BindingsSchema,
    gamepad: GamepadSettingsSchema,
    audio: AudioLevelsSchema,
    accessibility: AccessibilitySchema,
})
    .strict();
export const DEFAULT_USER_SETTINGS = {
    bindings: DEFAULT_BINDINGS,
    gamepad: DEFAULT_GAMEPAD,
    audio: DEFAULT_AUDIO,
    accessibility: DEFAULT_ACCESSIBILITY,
};
/** Bindings shared by two or more actions (the menu shows them; nothing is refused). */
export function bindingConflicts(bindings) {
    const users = new Map();
    for (const action of ACTIONS) {
        for (const b of bindings[action]) {
            const list = users.get(b) ?? [];
            if (!list.includes(action))
                list.push(action);
            users.set(b, list);
        }
    }
    return [...users.entries()]
        .filter(([, actions]) => actions.length > 1)
        .map(([binding, actions]) => ({ binding, actions }));
}
/** Replaces one slot of an action's bindings (slot 1 may be added; an empty slot is dropped). */
export function rebind(bindings, action, slot, binding) {
    const current = [...bindings[action]];
    if (binding === null) {
        if (current.length > 1)
            current.splice(slot, 1);
    }
    else {
        current[slot] = binding;
    }
    return { ...bindings, [action]: current };
}
/** A human label for a binding (`KeyW` -> `W`, `Mouse0` -> `Left click`). */
export function bindingLabel(binding) {
    const mouse = {
        Mouse0: 'Left click',
        Mouse1: 'Middle click',
        Mouse2: 'Right click',
        Mouse3: 'Mouse 4',
        Mouse4: 'Mouse 5',
    };
    if (binding in mouse)
        return mouse[binding] ?? binding;
    if (binding.startsWith('Key'))
        return binding.slice(3);
    if (binding.startsWith('Digit'))
        return binding.slice(5);
    if (binding.startsWith('Arrow'))
        return `${binding.slice(5)} arrow`;
    return binding.replace(/(Left|Right)$/, ' $1').replace(/([a-z])([A-Z])/g, '$1 $2');
}
//# sourceMappingURL=user-settings.js.map